// Game đua vịt (Duck Race) — sự kiện tương tác cho khách tại cửa hàng.
//
// Dữ liệu chia làm 2 tầng:
//  - Firestore  (`duck-race`)  : hồ sơ lâu dài — tên lượt đua, cấu hình, kết quả,
//                                nhà vô địch. Đây là CĂN CỨ NHẬN THƯỞNG.
//  - Realtime DB (`duck-race/`): trạng thái sống trong lúc đua — vị trí từng con
//                                vịt, pha hiện tại, mốc thời gian. Xoá sau khi xong.
//
// Soft delete + timestamp theo đúng quy ước các collection khác.

import { DuckRaceGift } from './duck-gift.model';

/**
 * `scheduled` = đã hẹn sẵn ngày giờ, chưa tới lúc chạy.
 *
 * Khác `lobby` ở chỗ mốc `raceStartAt`/`raceEndAt` đã được ghi sẵn từ lúc admin
 * đặt lịch, nên mọi máy tự tính được thời điểm xuất phát mà KHÔNG cần ai bấm
 * nút. Xem `DuckRaceLiveService.openLobby`.
 */
export type DuckRacePhase = 'lobby' | 'scheduled' | 'countdown' | 'racing' | 'finished';

/**
 * Số giây đếm ngược — CỐ ĐỊNH, không cho admin chỉnh.
 *
 * Tiếng đếm ngược (`assets/duck/sound/countdown.mp3`) là một bản đọc số từ 15
 * về 0. Cho admin đổi số giây thì con số trên màn hình và giọng đọc lệch nhau —
 * người chơi nghe "5" trong khi màn hình hiện "20". Muốn đổi thời lượng thì
 * phải thay cả file tiếng rồi sửa hằng số này cho khớp.
 *
 * `DuckRace.countdown_seconds` vẫn còn trong hồ sơ Firestore để đọc được các
 * lượt đua cũ, nhưng mọi nơi hiển thị và tính giờ đều dùng hằng số này.
 */
export const DUCK_COUNTDOWN_SECONDS = 15;

/**
 * Bảng kết quả sau khi đua xong hiện gì.
 *
 * `winner` — chỉ nhà vô địch. Dùng cho lượt đua đông người tại quán: danh sách
 * cả trăm dòng trên điện thoại chẳng ai đọc, mà chỉ cần biết ai cầm quà.
 * `top10`  — tốp 10 người về đầu. Người ngoài tốp vẫn thấy hạng của chính mình
 * ở phần trên màn hình kết quả.
 */
export type DuckResultDisplay = 'winner' | 'top10';

/** Số dòng của bảng kết quả khi chọn `top10`. */
export const DUCK_RESULT_TOP_COUNT = 10;

/** Hồ sơ một lượt đua, lưu Firestore. */
export interface DuckRace {
  id: string;
  name: string;
  /** Pha gần nhất được ghi lại (đồng bộ từ Realtime DB khi admin điều khiển). */
  status: DuckRacePhase;
  /** Số giây đếm ngược trước khi xuất phát. */
  countdown_seconds: number;
  /** Độ dài cuộc đua tính bằng giây. */
  duration_seconds: number;
  /** Hệ số tăng tốc cho con vịt được chọn buff ở 5 giây cuối. */
  buff_multiplier: number;
  /**
   * Có bốc con vịt được buff ở cuối chặng hay không.
   *
   * Tách khỏi `buff_multiplier` chứ không dùng ×1 để tắt: hệ số là con số admin
   * đã canh cho lượt đua này, tắt đi rồi bật lại mà phải nhớ đặt lại ×2 là
   * nguồn sai sót. Giữ cả hai thì tắt/bật không đụng tới hệ số đã đặt.
   */
  buff_enabled: boolean;
  /**
   * Có chạy băng quay quà cho người thắng sau khi đua xong hay không.
   *
   * Khác với `gifts` rỗng: lượt đua đã tick sẵn danh sách quà vẫn tắt được
   * vòng quay (ví dụ đua giao hữu, trao quà tay) mà không mất cấu hình.
   */
  gift_enabled: boolean;
  /** Bảng kết quả trên máy người chơi hiện gì sau khi đua xong. */
  result_display: DuckResultDisplay;
  /** Cho phép người chơi mới vào phòng chờ hay không. */
  join_open: boolean;
  /**
   * Ngày giờ hẹn sẵn cho lượt đua. `null` = chạy tay (admin bấm Bắt đầu).
   *
   * Đặt giá trị này là cuộc đua tự xuất phát đúng giờ, không cần ai bấm: mốc
   * thời gian được ghi vào Realtime DB ngay lúc lưu, và mỗi máy so đồng hồ máy
   * chủ với mốc đó. Xem `docs/DUCK_RACE.md` — mục *Hẹn giờ*.
   */
  scheduled_at: Date | null;
  player_count: number;
  winner_name: string;
  winner_phone: string;
  winner_color: string;
  /**
   * Những món quà tham gia vòng quay của lượt đua này (tick chọn từ kho tổng
   * `duck-gift`), kèm % trúng riêng. Rỗng ⇒ không hiện nút mở quà.
   */
  gifts: DuckRaceGift[];
  /**
   * Món người thắng đã quay trúng. Chép cứng tên và ảnh tại thời điểm quay —
   * đây là căn cứ trao thưởng nên không được đổi theo kho về sau.
   */
  winner_gift_id: string;
  winner_gift_name: string;
  winner_gift_image: string;
  results: DuckRaceResult[];
  started_at: Date | null;
  finished_at: Date | null;
  deleted: boolean;
  created_date: Date;
  updated_date: Date;
}

/** Một dòng trong bảng kết quả chung cuộc. */
export interface DuckRaceResult {
  rank: number;
  player_id: string;
  name: string;
  phone: string;
  color: string;
  /** Tiến độ 0..1 tại thời điểm kết thúc. */
  progress: number;
  /** Con vịt này có được hệ thống buff ở 5 giây cuối không. */
  buffed: boolean;
}

// ===================== Đọc cấu hình (có đường lui cho hồ sơ cũ) =====================
//
// Ba công tắc dưới đây thêm vào sau khi đã có lượt đua chạy thật, nên hồ sơ cũ
// trong Firestore KHÔNG có field tương ứng — đọc thẳng ra `undefined`. Mọi nơi
// phải đi qua các hàm này thay vì đọc field trực tiếp, để `undefined` rơi về
// đúng hành vi mà lượt đua cũ vẫn đang có chứ không rơi về "tắt".

/** Lượt đua này có bốc con vịt được buff cuối chặng không. */
export function duckBuffEnabled(race: { buff_enabled?: boolean } | null | undefined): boolean {
  return race?.buff_enabled !== false;
}

/** Lượt đua này có chạy băng quay quà cho người thắng không. */
export function duckGiftEnabled(race: { gift_enabled?: boolean } | null | undefined): boolean {
  return race?.gift_enabled !== false;
}

/** Bảng kết quả của lượt đua này hiện gì. */
export function duckResultDisplay(
  race: { result_display?: DuckResultDisplay } | null | undefined
): DuckResultDisplay {
  return race?.result_display === 'winner' ? 'winner' : 'top10';
}

/**
 * Hệ số buff ĐANG THỰC SỰ có hiệu lực — ×1 khi công tắc buff tắt.
 *
 * Quy về một con số ngay tại đây rồi ghi vào `meta` để máy người chơi không
 * phải biết tới công tắc: `stepMotion` cứ nhân với hệ số nhận được là xong.
 * Trang chơi đọc `meta` từ Realtime DB chứ không đọc hồ sơ Firestore, nên nếu
 * để nó tự suy ra thì mỗi màn hình lại cần thêm một đường dữ liệu nữa.
 */
export function duckBuffMultiplier(
  race: { buff_enabled?: boolean; buff_multiplier?: number } | null | undefined
): number {
  if (!duckBuffEnabled(race)) return 1;
  return race?.buff_multiplier || 1;
}

// ===================== Trạng thái sống trên Realtime Database =====================

/**
 * Mốc điều khiển cuộc đua. Mọi thời điểm là **giờ máy chủ** (ms) chứ không phải
 * giờ máy người chơi — điện thoại mỗi người lệch giờ khác nhau, nên phải quy về
 * một đồng hồ chung (xem `DuckRaceLiveService.serverNow`).
 */
export interface DuckLiveMeta {
  phase: DuckRacePhase;
  /** Giờ hẹn xuất phát (0 = không hẹn, chờ admin bấm tay). */
  scheduledAt: number;
  /** Thời điểm hết đếm ngược = thời điểm xuất phát. */
  raceStartAt: number;
  /** Thời điểm cuộc đua kết thúc. */
  raceEndAt: number;
  durationMs: number;
  buffMultiplier: number;
  /** Id con vịt được chọn buff (rỗng nếu chưa tới lúc chọn). */
  buffPlayerId: string;
  /** Thời điểm hiệu lực buff (raceEndAt - 5s). */
  buffStartAt: number;
  joinOpen: boolean;
}

/** Một con vịt đang đua. Mỗi người chơi chỉ ghi node của chính mình. */
export interface DuckLivePlayer {
  id: string;
  name: string;
  color: string;
  /** Tiến độ 0..1 trên đường đua. */
  pos: number;
  /** Tổng số lần bấm — dùng để hiển thị và phát hiện bất thường. */
  taps: number;
  /** Thời điểm cán đích (0 nếu chưa về). */
  finishedAt: number;
  joinedAt: number;
  /**
   * Số điện thoại người chơi, **ĐÃ CHE SẴN** (`09*****678`).
   *
   * Nhánh `players` trên Realtime DB ai đọc cũng được — mọi máy đang đua đều
   * phải đọc để vẽ các con vịt. Ghi số đầy đủ vào đây là mở kho số điện thoại
   * khách hàng cho bất kỳ ai mở trang. Nên máy người chơi tự che TRƯỚC KHI gửi:
   * thứ rời khỏi điện thoại họ đã là bản che rồi.
   *
   * Đổi lại, phòng điều khiển cũng chỉ thấy bản che. Muốn số đầy đủ trong trang
   * quản trị thì phải có một đường ghi riêng mà khách không đọc được, tức là
   * cần Cloud Functions — dự án này hiện không có.
   */
  phone?: string;

  /**
   * Đã rời cuộc đua — đóng tab, bấm "Rời phòng", hoặc mất kết nối.
   *
   * Đánh dấu chứ không xoá hẳn node: rule của Realtime DB không cho khách vãng
   * lai XOÁ (xem `database.rules.json`) — nếu mở quyền xoá thì ai cũng xoá được
   * vịt của người khác giữa cuộc đua. Ghi thêm một trường thì vẫn nằm trong
   * quyền "tạo và cập nhật" sẵn có.
   *
   * Mọi màn hình lọc bỏ người có cờ này ngay trong `watchPlayers`/`readPlayers`,
   * nên phần còn lại của ứng dụng coi như họ không tồn tại.
   */
  left?: boolean;
}

// ===================== Màu vịt =====================

export interface DuckColorDef {
  key: string;
  label: string;
  /** Màu thân (tông giữa). */
  body: string;
  /** Màu bụng — sáng hơn thân, dùng cho vùng bắt sáng. */
  belly: string;
  /** Tông tối — dùng đổ khối và viền cho con vịt có chiều sâu. */
  dark: string;
  /** Màu mỏ. */
  beak: string;
}

export const DUCK_COLORS: DuckColorDef[] = [
  { key: 'yellow', label: 'Vàng', body: '#F8D008', belly: '#FCEA90', dark: '#9A8105', beak: '#FF9E1B' },
  { key: 'blue', label: 'Xanh dương', body: '#28B8F8', belly: '#9EDFFC', dark: '#19729A', beak: '#FF9E1B' },
  { key: 'pink', label: 'Hồng', body: '#F890A0', belly: '#FCCDD4', dark: '#9A5963', beak: '#FF9E1B' },
  { key: 'green', label: 'Xanh lá', body: '#A0F018', belly: '#D4F897', dark: '#63950F', beak: '#FF9E1B' },
  { key: 'purple', label: 'Tím', body: '#B868F8', belly: '#DFBBFC', dark: '#72409A', beak: '#FF9E1B' },
  { key: 'orange', label: 'Cam', body: '#F89010', belly: '#FCCD93', dark: '#9A590A', beak: '#E2571E' },
  { key: 'white', label: 'Trắng', body: '#F0F0F0', belly: '#FFFFFF', dark: '#959595', beak: '#FF9E1B' },
  { key: 'black', label: 'Đen', body: '#383838', belly: '#A5A5A5', dark: '#232323', beak: '#FFC93C' },
  { key: 'red', label: 'Đỏ', body: '#F84030', belly: '#FCA9A2', dark: '#9A281E', beak: '#FFB020' },
  { key: 'grey', label: 'Xám', body: '#A8A8A8', belly: '#D8D8D8', dark: '#686868', beak: '#FF9E1B' },
];

/**
 * Dùng ảnh thật thay cho hình SVG vẽ tay.
 *
 * Ảnh nằm ở `src/assets/duck/<key>.webp` — 8 file, 400x495, nền trong suốt,
 * vịt quay mặt sang PHẢI, đã cắt sát viền rồi căn giữa về cùng một khổ.
 * Bước căn khổ chung là bắt buộc: nếu mỗi ảnh một khổ riêng thì trên đường đua
 * con thừa nền sẽ trông bé hơn hẳn con cắt sát.
 *
 * WebP thay vì PNG: cùng độ nét nhưng nhẹ hơn 5 lần (225 KB cả bộ thay vì
 * 1,35 MB) — đáng kể với khách mở bằng mạng di động tại cửa hàng.
 *
 * Đặt `false` để quay lại hình SVG vẽ tay (vẫn còn nguyên trong
 * `DuckSpriteComponent`, dùng khi cần thêm màu mới mà chưa có ảnh).
 *
 * ⚠️ Bảng màu bên dưới phải khớp đúng những file có thật. Thêm màu vào
 * `DUCK_COLORS` mà quên bỏ file ảnh vào thì màu đó sẽ hiện ảnh vỡ.
 *
 * ⚠️ Bốn màu `body`/`belly`/`dark`/`beak` chỉ dùng cho bản vẽ SVG dự phòng
 * (`DUCK_USE_IMAGES = false`). Bản SVG vẽ vịt trơn, KHÔNG có biểu cảm riêng.
 */
export const DUCK_USE_IMAGES = true;

/** Đường dẫn ảnh của một màu vịt. */
export function duckImageUrl(key: string): string {
  return `assets/duck/${key}.webp`;
}

export function duckColor(key: string): DuckColorDef {
  return DUCK_COLORS.find((c) => c.key === key) ?? DUCK_COLORS[0];
}

// ===================== Mốc thời gian =====================

/**
 * Đổi một mốc thời gian của Firestore về mili giây.
 *
 * Cùng một field đọc ra ba kiểu khác nhau tuỳ đường đi: `Timestamp` khi lấy từ
 * máy chủ, `Date` khi vừa ghi xong ở máy này (cache trả lại nguyên vật), và
 * `{seconds}` khi đi qua một vòng JSON. So sánh thẳng là sai lúc này lúc khác.
 */
export function firestoreMillis(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  const ts = value as { toMillis?: () => number; seconds?: number };
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

/** Giờ hẹn của lượt đua (ms). 0 nghĩa là không hẹn giờ. */
export function raceScheduledMs(race: { scheduled_at?: Date | null }): number {
  return firestoreMillis(race?.scheduled_at);
}

/**
 * Giờ hẹn dạng chữ: "20:00 · 05/09/2026". Trả '' khi không có hẹn.
 *
 * Trả CHUỖI chứ không trả `Date` cho template: getter trả object mới mỗi vòng
 * change detection thì `*ngIf` thấy tham chiếu khác nhau và dựng lại khối đó
 * liên tục — đúng kiểu đã từng làm đơ hẳn một trang trong dự án này. Chuỗi so
 * sánh theo giá trị nên đứng yên.
 */
export function formatDateTime(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())} · ${pad(d.getDate())}/${pad(
    d.getMonth() + 1
  )}/${d.getFullYear()}`;
}

/**
 * Đếm ngược dạng chữ: "2 ngày 05:12:33" / "05:12" / "42 giây".
 * Trả '' khi đã tới giờ — nơi gọi tự quyết định hiện gì thay vào đó.
 */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return '';
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (days > 0) return `${days} ngày ${pad(h)}:${pad(m)}:${pad(s)}`;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  if (m > 0) return `${pad(m)}:${pad(s)}`;
  return `${s} giây`;
}

// ===================== Số điện thoại =====================

/** Bỏ khoảng trắng, dấu chấm/gạch và đổi tiền tố +84 → 0. */
export function normalizeVnPhone(input: string): string {
  const raw = (input || '').replace(/[\s.\-()]/g, '');
  if (raw.startsWith('+84')) return '0' + raw.slice(3);
  if (raw.startsWith('84') && raw.length === 11) return '0' + raw.slice(2);
  return raw;
}

/** Kiểm tra định dạng số di động Việt Nam (10 số, đầu 03/05/07/08/09). */
export function isValidVnPhone(input: string): boolean {
  const p = normalizeVnPhone(input);
  return /^0(3[2-9]|5[25689]|7[06-9]|8[1-9]|9[0-9])\d{7}$/.test(p);
}

/**
 * Che giữa số điện thoại khi hiển thị công khai: 0912345678 → 09*****678.
 *
 * Chỉ để lại 2 số đầu và 3 số đuôi. Người trúng nhìn là nhận ra số mình, còn
 * người ngoài thì không ghép đủ để gọi làm phiền họ.
 */
export function maskPhone(input: string): string {
  const p = normalizeVnPhone(input);
  if (p.length < 6) return p;
  return p.slice(0, 2) + '*'.repeat(p.length - 5) + p.slice(-3);
}
